import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useInView, useReducedMotion } from 'framer-motion';

interface CounterProps {
  value: number;
  suffix?: string;
  duration?: number; // seconds
}

/**
 * Counts up from 0 to `value` once it scrolls into view.
 * Under reduced-motion it renders the final value immediately.
 */
const Counter: React.FC<CounterProps> = ({ value, suffix = '', duration = 1.6 }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const prefersReduced = useReducedMotion();
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (prefersReduced) {
      setDisplay(value);
      return;
    }

    let raf = 0;
    const start = performance.now();
    // cubic-bezier(ease.out) approximated via easeOutCubic for the count
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (now: number) => {
      const t = Math.min((now - start) / (duration * 1000), 1);
      setDisplay(Math.round(easeOut(t) * value));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration, prefersReduced]);

  return (
    <Box
      component="span"
      ref={ref}
      sx={{
        fontFamily: (t) => t.typography.h1.fontFamily,
        fontWeight: 600,
      }}
    >
      {display}
      {suffix}
    </Box>
  );
};

export default Counter;
